/*
 * The Tickets screen's state, shared by the classic panel and the v3 screen.
 *
 * The save model is lifted verbatim and deliberately unchanged. Edits accumulate as a draft per
 * ticket and one save commits them all with `Promise.allSettled`, keeping the drafts of whichever
 * rows failed so nobody retypes a price because a different row was rejected. The frame moves
 * *where* editing happens — a drawer instead of an inline row — and nothing about what is sent.
 *
 * Wristband bindings travel with the ticket for the same reason they do today: a ticket type and
 * the bands it hands out are one decision, and saving one without the other is how a buyer ends up
 * with a ticket the door cannot band.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type {
  AdminTicketDto, TicketInput, WristbandBinding, WristbandDto,
} from '../../api';
import { confirmFreeTickets, isFreeOnlineTicket } from '../../lib/freeTicket';

export type TicketDraftFields = Pick<
  TicketInput,
  'nameEn' | 'nameTh' | 'priceRegular' | 'priceEarly' | 'capacity' | 'seats'
  | 'promotion' | 'offline' | 'serialPrefix' | 'customFields' | 'seatingMode'
>;

export function ticketDraftOf(t: AdminTicketDto): TicketDraftFields {
  return {
    nameEn: t.nameEn, nameTh: t.nameTh, priceRegular: t.priceRegular, priceEarly: t.priceEarly,
    capacity: t.capacity, seats: t.seats, promotion: t.promotion, offline: t.offline,
    serialPrefix: t.serialPrefix ?? null, customFields: t.customFields,
    seatingMode: t.seatingMode,
  };
}

const sortedBindings = (rows: readonly WristbandBinding[]) =>
  JSON.stringify([...rows].filter((r) => r.qty > 0).sort((a, b) => a.wristbandId.localeCompare(b.wristbandId)));

export interface TicketsState {
  tickets: AdminTicketDto[];
  wristbands: WristbandDto[];
  bindings: Record<string, WristbandBinding[]>;
  drafts: Record<string, TicketDraftFields>;
  bandDrafts: Record<string, WristbandBinding[]>;
  loading: boolean;
  loadError: string;
  saving: boolean;
  justSaved: boolean;
  saveError: string;
  anyDirty: boolean;
  isDirty: (id: string) => boolean;
  setDraft: (id: string, patch: Partial<TicketDraftFields>) => void;
  setBandDraft: (id: string, rows: WristbandBinding[]) => void;
  discard: (id: string) => void;
  saveAll: () => Promise<void>;
  create: (input: TicketInput) => Promise<boolean>;
  creating: boolean;
  createError: string;
  reload: () => Promise<void>;
}

export function useTickets(token: string, eventSlug?: string): TicketsState {
  const [tickets, setTickets] = useState<AdminTicketDto[] | null>(null);
  const [wristbands, setWristbands] = useState<WristbandDto[]>([]);
  const [bindings, setBindings] = useState<Record<string, WristbandBinding[]>>({});
  const [drafts, setDrafts] = useState<Record<string, TicketDraftFields>>({});
  const [bandDrafts, setBandDrafts] = useState<Record<string, WristbandBinding[]>>({});
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const load = useCallback(async () => {
    try {
      const [list, bands, bound] = await Promise.all([
        adminApi.listTickets(token, eventSlug),
        adminApi.listWristbands(token, eventSlug),
        adminApi.getTicketWristbands(token, eventSlug),
      ]);
      setTickets(list);
      setWristbands(bands);
      setBindings(bound);
      setLoadError('');
    } catch {
      setLoadError('โหลดข้อมูลตั๋วไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  // A draft exists for every ticket, seeded from the server row and kept across reloads if the
  // person has touched it — a refresh in the background must not erase a half-typed price.
  useEffect(() => {
    if (!tickets) return;
    setDrafts((prev) => {
      const next: Record<string, TicketDraftFields> = {};
      for (const t of tickets) next[t.id] = prev[t.id] ?? ticketDraftOf(t);
      return next;
    });
    setBandDrafts((prev) => {
      const next: Record<string, WristbandBinding[]> = {};
      for (const t of tickets) {
        next[t.id] = prev[t.id] ?? (bindings[t.id] ?? []).filter((b) => b.qty > 0).map((b) => ({ ...b }));
      }
      return next;
    });
  }, [tickets, bindings]);

  const ticketDirty = (t: AdminTicketDto) => {
    const d = drafts[t.id];
    return !!d && JSON.stringify(d) !== JSON.stringify(ticketDraftOf(t));
  };
  const bandDirty = (t: AdminTicketDto) => sortedBindings(bandDrafts[t.id] ?? []) !== sortedBindings(bindings[t.id] ?? []);
  const list = tickets ?? [];
  const anyDirty = list.some((t) => ticketDirty(t) || bandDirty(t));

  const saveAll = useCallback(async () => {
    if (!tickets) return;
    const dirtyTickets = tickets.filter(ticketDirty);
    // TICK-146 — asked once, for every type this save would turn free.
    const newlyFree = dirtyTickets.filter((t) => !isFreeOnlineTicket(t) && isFreeOnlineTicket(drafts[t.id]));
    if (!confirmFreeTickets(newlyFree.map((t) => drafts[t.id].nameTh || drafts[t.id].nameEn))) return;
    setSaving(true);
    setSaveError('');
    setJustSaved(false);
    const dirtyBands = tickets.filter(bandDirty);
    const failedTickets = new Set<string>();
    const failedBands = new Set<string>();
    const failures: string[] = [];

    await Promise.allSettled([
      ...dirtyTickets.map(async (t) => {
        try {
          await adminApi.updateTicket(token, t.id, drafts[t.id], eventSlug);
        } catch (e) {
          failedTickets.add(t.id);
          failures.push(`${t.nameEn || t.id}: ${e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ'}`);
        }
      }),
      ...dirtyBands.map(async (t) => {
        try {
          await adminApi.setTicketWristbands(
            token, t.id, (bandDrafts[t.id] ?? []).filter((r) => r.wristbandId && r.qty > 0), eventSlug,
          );
        } catch (e) {
          failedBands.add(t.id);
          failures.push(`Wristband ${t.nameEn || t.id}: ${e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ'}`);
        }
      }),
    ]);

    await load();
    // Only the rows that committed drop their draft. A row that was rejected keeps exactly what
    // was typed, because that is the row the message is about.
    setDrafts((prev) => {
      const next = { ...prev };
      for (const t of dirtyTickets) if (!failedTickets.has(t.id)) delete next[t.id];
      return next;
    });
    setBandDrafts((prev) => {
      const next = { ...prev };
      for (const t of dirtyBands) if (!failedBands.has(t.id)) delete next[t.id];
      return next;
    });
    setSaving(false);
    if (failures.length) setSaveError(failures.join(' · '));
    else setJustSaved(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, drafts, bandDrafts, bindings, token, eventSlug, load]);

  return {
    tickets: list, wristbands, bindings, drafts, bandDrafts,
    loading: tickets === null && !loadError,
    loadError, saving, justSaved, saveError, anyDirty,
    isDirty: (id) => {
      const t = list.find((x) => x.id === id);
      return !!t && (ticketDirty(t) || bandDirty(t));
    },
    setDraft: (id, patch) => {
      setJustSaved(false);
      setDrafts((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...patch } } : prev));
    },
    setBandDraft: (id, rows) => {
      setJustSaved(false);
      setBandDrafts((prev) => ({ ...prev, [id]: rows }));
    },
    discard: (id) => {
      const t = list.find((x) => x.id === id);
      if (!t) return;
      setDrafts((prev) => ({ ...prev, [id]: ticketDraftOf(t) }));
      setBandDrafts((prev) => ({ ...prev, [id]: (bindings[id] ?? []).filter((b) => b.qty > 0).map((b) => ({ ...b })) }));
      setJustSaved(false);
    },
    saveAll,
    creating, createError,
    create: async (input) => {
      if (isFreeOnlineTicket(input) && !confirmFreeTickets([input.nameTh || input.nameEn || 'ประเภทตั๋วใหม่'])) return false;
      setCreating(true);
      setCreateError('');
      try {
        const nextSort = list.length ? Math.max(...list.map((t) => t.sort ?? 0)) + 1 : 0;
        await adminApi.createTicket(token, { ...input, sort: nextSort }, eventSlug);
        await load();
        return true;
      } catch (e) {
        setCreateError(e instanceof ApiError ? e.message : 'สร้างประเภทบัตรไม่สำเร็จ');
        return false;
      } finally {
        setCreating(false);
      }
    },
    reload: load,
  };
}
